import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stethoscope, ChartLine, Plus, Trash, DownloadSimple, Eye, EyeSlash,
  Calendar, FileText, ListChecks, Clipboard, CheckCircle, Circle, FilePdf,
} from "@phosphor-icons/react";
import { listAppointments, listPrescriptions } from "@/lib/api";
import { downloadTextFile, saveBytesAsPdf } from "@/lib/claimUtils";
import { buildVisitMarkdown, getTalkingPoints, VISIT_TYPES, isClaimVisit } from "@/lib/imeMarkdown";
import { createImeIntakePdf } from "@/lib/imeIntakePdf";
import { showToast } from "@/lib/toast";

const TABS = [
  { id: "dashboard",  label: "Dashboard",         icon: ChartLine },
  { id: "timeline",   label: "Timeline",          icon: Calendar },
  { id: "checklists", label: "Talking Points",    icon: ListChecks },
  { id: "documents",  label: "Records to Bring",  icon: FileText },
];

function todayIso() { return new Date().toISOString().slice(0, 10); }

/**
 * Visit Prep packet — produces a clean markdown/PDF summary a veteran can bring
 * to (or email ahead of) any doctor appointment: primary care, specialist
 * consult, therapy, C&P exam, Independent Medical Exam, or anything else.
 *
 * Storage key on the workspace remains `imeIntake` for backward compatibility
 * with previously saved workspaces; the field names below extend that shape:
 *   visitType, visitDate, visitReason  (new — visit-generic)
 *   providerName, providerSpecialty    (new — replace examinerName/specialty)
 *   discussionQuestions                (new — replaces examinerQuestions)
 *   serviceHistory, timeline, checklists, documents  (unchanged)
 * Old `examinerName/Specialty/Questions` keys still read as fallbacks.
 */
export default function ImeIntakeView({ profile, master526ez, issues = [], evidence = [], statements = [], imeIntake = {}, setImeIntake }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, p] = await Promise.all([listAppointments(), listPrescriptions()]);
        if (!alive) return;
        setAppointments(a);
        setPrescriptions(p);
      } catch (err) { console.error("visit-prep: load appts/rx failed", err); }
    })();
    return () => { alive = false; };
  }, []);

  // Default visit type the first time the user opens this view.
  useEffect(() => {
    if (!imeIntake.visitType) setImeIntake({ ...(imeIntake || {}), visitType: "general" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo(() => ({
    profile, master526ez, issues, evidence, appointments, prescriptions, statements, imeIntake,
  }), [profile, master526ez, issues, evidence, appointments, prescriptions, statements, imeIntake]);
  const md = useMemo(() => buildVisitMarkdown(data), [data]);
  const visitTypeId = imeIntake.visitType || "general";
  const visitTypeDef = useMemo(
    () => VISIT_TYPES.find((v) => v.id === visitTypeId) || VISIT_TYPES[0],
    [visitTypeId],
  );

  const patch = useCallback((patchObj) => {
    setImeIntake({ ...(imeIntake || {}), ...patchObj });
  }, [imeIntake, setImeIntake]);

  function fileStem() {
    const stamp = todayIso();
    const safeName = ((profile.name || master526ez.veteranName || "Veteran").trim() || "Veteran")
      .replace(/[^a-zA-Z0-9-_]+/g, "-");
    return `ClaimOps-Visit-Prep-${safeName}-${stamp}`;
  }

  function downloadMarkdown() {
    downloadTextFile(`${fileStem()}.md`, md);
    showToast({ variant: "success", message: "Visit prep markdown downloaded.", duration: 3000 });
  }

  async function downloadPdf() {
    try {
      const bytes = await createImeIntakePdf(data);
      saveBytesAsPdf(bytes, `${fileStem()}.pdf`);
      showToast({ variant: "success", message: "Visit prep PDF downloaded.", duration: 3000 });
    } catch (err) {
      console.error(err);
      showToast({ variant: "error", message: `PDF export failed: ${err.message}` });
    }
  }

  async function copyMarkdown() {
    try {
      await navigator.clipboard.writeText(md);
      showToast({ variant: "success", message: "Markdown copied to clipboard.", duration: 2500 });
    } catch (e) {
      showToast({ variant: "error", message: `Copy failed: ${e.message}` });
    }
  }

  return (
    <div className="view-grid" data-testid="ime-view">
      <div className="panel">
        <div className="panel-header">
          <Stethoscope size={18} weight="bold" />
          <span>VISIT PREP PACKET</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc" style={{ marginTop: 0 }}>
            Build a clean, doctor-ready packet for any upcoming appointment — primary care follow-up, specialist consult, therapy session, C&amp;P exam, IME, or anything else. Everything you&apos;ve already entered (profile, conditions, evidence, statements, appointments, meds) is auto-populated. Pick a visit type below and the prompts adapt: routine visits keep things short, claim-related visits unlock the deeper service-connection sections.
          </p>

          <VisitTypeSelector value={visitTypeId} onChange={(id) => patch({ visitType: id })} />

          <div role="tablist" aria-label="Visit prep tabs" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={active}
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`ime-tab-${t.id}`}
                  style={{
                    background: active ? "var(--branch-accent)" : "var(--surface-2)",
                    color: active ? "#fff" : "var(--text-primary)",
                    border: "1px solid",
                    borderColor: active ? "var(--branch-accent)" : "var(--border)",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
                    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                    transition: "background-color 180ms ease, color 180ms ease, border-color 180ms ease",
                  }}
                >
                  <Icon size={13} weight="bold" /> {t.label.toUpperCase()}
                </button>
              );
            })}
            <div className="ime-toolbar-actions" style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button type="button" className="btn btn--outline btn--sm" onClick={() => setPreviewOpen((p) => !p)} data-testid="ime-preview-toggle">
                {previewOpen ? <EyeSlash size={11} weight="bold" /> : <Eye size={11} weight="bold" />}
                {previewOpen ? " HIDE PREVIEW" : " SHOW PREVIEW"}
              </button>
              <button type="button" className="btn btn--outline btn--sm" onClick={copyMarkdown} data-testid="ime-copy">
                <Clipboard size={11} weight="bold" /> COPY MD
              </button>
              <button type="button" className="btn btn--outline btn--sm" onClick={downloadMarkdown} data-testid="ime-download">
                <DownloadSimple size={11} weight="bold" /> DOWNLOAD .MD
              </button>
              <button type="button" className="btn btn--accent btn--sm" onClick={downloadPdf} data-testid="ime-download-pdf">
                <FilePdf size={11} weight="bold" /> DOWNLOAD PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeTab === "dashboard" && (
        <VisitDashboard
          profile={profile} master526ez={master526ez} issues={issues} evidence={evidence}
          statements={statements} appointments={appointments} prescriptions={prescriptions}
          imeIntake={imeIntake} patch={patch} visitTypeDef={visitTypeDef}
        />
      )}
      {activeTab === "timeline" && (
        <TimelineBuilder imeIntake={imeIntake} patch={patch} appointments={appointments} master526ez={master526ez} visitTypeDef={visitTypeDef} />
      )}
      {activeTab === "checklists" && (
        <TalkingPoints issues={issues} imeIntake={imeIntake} patch={patch} visitTypeDef={visitTypeDef} />
      )}
      {activeTab === "documents" && (
        <DocumentManager evidence={evidence} imeIntake={imeIntake} patch={patch} />
      )}

      {previewOpen && (
        <div className="panel" data-testid="ime-preview-panel">
          <div className="panel-header">
            <FileText size={16} weight="bold" />
            <span>MARKDOWN PREVIEW</span>
            <span className="header-count mono" data-testid="ime-md-size">{md.length.toLocaleString()} chars</span>
          </div>
          <div className="panel-body">
            <pre
              data-testid="ime-md-preview"
              style={{
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 14,
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 11,
                lineHeight: 1.55,
                maxHeight: 560,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "var(--text-primary)",
              }}
            >
              {md}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================ VISIT-TYPE CHIPS
function VisitTypeSelector({ value, onChange }) {
  return (
    <div data-testid="ime-visit-type" style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 8 }}>
        Visit type
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {VISIT_TYPES.map((v) => {
          const active = value === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => onChange(v.id)}
              data-testid={`ime-visit-type-${v.id}`}
              aria-pressed={active}
              style={{
                background: active ? "var(--branch-accent-soft)" : "var(--surface)",
                color: active ? "var(--branch-accent)" : "var(--text-secondary)",
                border: "1px solid",
                borderColor: active ? "var(--branch-accent)" : "var(--border)",
                borderRadius: 999,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: active ? 700 : 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                transition: "all 160ms ease",
              }}
            >
              <span>{v.label}</span>
              {v.claimRelated && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.75 }}>· Claim</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================ DASHBOARD TAB
function VisitDashboard({ profile, master526ez, issues, evidence, statements, appointments, prescriptions, imeIntake, patch, visitTypeDef }) {
  const claimRelated = isClaimVisit(visitTypeDef);
  const counts = {
    conditions: issues.length,
    evidence: evidence.length,
    statements: statements.length,
    appointments: appointments.length,
    medications: prescriptions.filter((rx) => !rx.archived).length,
    timeline: (imeIntake.timeline || []).length,
    documents: (imeIntake.documents || []).length,
  };
  const hasQuestions = !!((imeIntake.discussionQuestions || imeIntake.examinerQuestions || "").trim());
  const readiness = [
    { label: "Visit type selected", done: !!imeIntake.visitType },
    { label: "Visit date set", done: !!imeIntake.visitDate },
    { label: "Reason for visit written", done: !!(imeIntake.visitReason || "").trim() },
    { label: "≥ 1 talking point / question", done: hasQuestions },
    ...(claimRelated ? [
      { label: "≥ 1 condition", done: counts.conditions > 0 },
      { label: "≥ 1 piece of evidence", done: counts.evidence > 0 },
    ] : []),
  ];
  const ready = readiness.filter((r) => r.done).length;
  void profile; void master526ez; void statements; // referenced in markdown, not in this card

  return (
    <div className="panel" data-testid="ime-dashboard">
      <div className="panel-header">
        <ChartLine size={16} weight="bold" />
        <span>PACKET DASHBOARD</span>
        <span className="header-count mono">{ready}/{readiness.length} READY</span>
      </div>
      <div className="panel-body">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 18 }}>
          {Object.entries(counts).map(([k, v]) => (
            <div key={k} className="metric-card" data-testid={`ime-kpi-${k}`} style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
              <div className="metric-card__label" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", color: "var(--text-secondary)", textTransform: "uppercase" }}>{k}</div>
              <div className="metric-card__value" style={{ fontSize: 22, fontWeight: 700, fontFamily: "Chivo, sans-serif", color: "var(--text-primary)" }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 8 }}>Packet readiness</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 6 }}>
            {readiness.map((r) => (
              <li key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)" }}>
                {r.done
                  ? <CheckCircle size={16} weight="fill" color="#10B981" />
                  : <Circle size={16} weight="bold" color="var(--text-tertiary)" />}
                <span style={{ opacity: r.done ? 0.65 : 1, textDecoration: r.done ? "line-through" : "none" }}>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 180px", gap: 12 }}>
          <label className="field">
            <span className="field-label">PROVIDER NAME</span>
            <input
              className="field-input"
              type="text"
              value={imeIntake.providerName || imeIntake.examinerName || ""}
              onChange={(e) => patch({ providerName: e.target.value, examinerName: e.target.value })}
              placeholder="Dr. Jane Smith, MD"
              data-testid="ime-examiner-name"
            />
          </label>
          <label className="field">
            <span className="field-label">SPECIALTY / CLINIC</span>
            <input
              className="field-input"
              type="text"
              value={imeIntake.providerSpecialty || imeIntake.examinerSpecialty || ""}
              onChange={(e) => patch({ providerSpecialty: e.target.value, examinerSpecialty: e.target.value })}
              placeholder="Primary care · Audiology · Psychiatry · Orthopedics..."
              data-testid="ime-examiner-specialty"
            />
          </label>
          <label className="field">
            <span className="field-label">VISIT DATE</span>
            <input
              className="field-input"
              type="date"
              value={imeIntake.visitDate || ""}
              onChange={(e) => patch({ visitDate: e.target.value })}
              data-testid="ime-visit-date"
            />
          </label>
        </div>

        <label className="field" style={{ marginTop: 14 }}>
          <span className="field-label">REASON FOR THIS VISIT</span>
          <textarea
            className="field-input"
            rows={2}
            value={imeIntake.visitReason || ""}
            onChange={(e) => patch({ visitReason: e.target.value })}
            placeholder={visitTypeDef.reasonPlaceholder}
            data-testid="ime-visit-reason"
          />
        </label>

        {claimRelated && (
          <label className="field" style={{ marginTop: 14 }}>
            <span className="field-label">SERVICE HISTORY NARRATIVE (1–2 paragraphs)</span>
            <textarea
              className="field-input"
              rows={4}
              value={imeIntake.serviceHistory || ""}
              onChange={(e) => patch({ serviceHistory: e.target.value })}
              placeholder="e.g., Enlisted USMC 2002, deployed to Iraq 2004 and 2007, infantry MOS, separated as a Sergeant in 2010. Multiple combat-arms exposures..."
              data-testid="ime-service-history"
            />
          </label>
        )}

        <label className="field" style={{ marginTop: 14 }}>
          <span className="field-label">{claimRelated ? "QUESTIONS FOR THE PROVIDER (one per line)" : "TOPICS / QUESTIONS TO COVER (one per line)"}</span>
          <textarea
            className="field-input"
            rows={5}
            value={imeIntake.discussionQuestions || imeIntake.examinerQuestions || ""}
            onChange={(e) => patch({ discussionQuestions: e.target.value, examinerQuestions: e.target.value })}
            placeholder={visitTypeDef.questionsPlaceholder}
            data-testid="ime-examiner-questions"
          />
        </label>
      </div>
    </div>
  );
}

// ============================================================================ TIMELINE TAB
function TimelineBuilder({ imeIntake, patch, appointments, master526ez, visitTypeDef }) {
  const timeline = imeIntake.timeline || [];
  const [form, setForm] = useState({ date: todayIso(), event: "", source: "" });
  const claimRelated = isClaimVisit(visitTypeDef);

  function addEvent() {
    const next = { ...form };
    if (!next.event.trim()) return;
    patch({ timeline: [...timeline, { id: `tl-${Date.now()}`, ...next }] });
    setForm({ date: todayIso(), event: "", source: "" });
  }
  function removeEvent(id) {
    patch({ timeline: timeline.filter((t) => t.id !== id) });
  }

  const merged = useMemo(() => {
    const out = [];
    if (claimRelated && master526ez.serviceStart) out.push({ id: "auto-ss", date: master526ez.serviceStart, event: "Service start", source: "Profile (auto)", auto: true });
    if (claimRelated && master526ez.serviceEnd) out.push({ id: "auto-se", date: master526ez.serviceEnd, event: "Service end / separation", source: "Profile (auto)", auto: true });
    appointments.filter((a) => a.completed).forEach((a) => {
      out.push({
        id: `auto-${a.id}`,
        date: (a.datetime || "").slice(0, 10),
        event: `${a.type === "cp_exam" ? "C&P Exam" : "Medical visit"}: ${a.title || "Appointment"}${a.provider ? ` (${a.provider})` : ""}`,
        source: "Appointment record (auto)",
        auto: true,
      });
    });
    timeline.forEach((t) => out.push({ ...t, auto: false }));
    return out.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [appointments, timeline, master526ez.serviceStart, master526ez.serviceEnd, claimRelated]);

  return (
    <div className="panel" data-testid="ime-timeline">
      <div className="panel-header">
        <Calendar size={16} weight="bold" />
        <span>TIMELINE</span>
        <span className="header-count mono">{merged.length} events</span>
      </div>
      <div className="panel-body">
        <p className="panel-desc" style={{ marginTop: 0 }}>
          {claimRelated
            ? "A clean chronology helps the examiner connect dots fast. Service dates and completed appointments are auto-seeded; add deployments, injuries, civilian doctor visits, ER trips, or any other dated event the provider should know about."
            : "Optional — add a few dated events the doctor should see (recent ER visit, new medication start, last imaging, etc.). Completed appointments are auto-seeded."}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr auto", gap: 8, alignItems: "end", padding: "12px 0", borderTop: "1px dashed var(--border)", borderBottom: "1px dashed var(--border)" }}>
          <label className="field">
            <span className="field-label">DATE</span>
            <input className="field-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="ime-tl-date" />
          </label>
          <label className="field">
            <span className="field-label">EVENT</span>
            <input className="field-input" type="text" value={form.event} onChange={(e) => setForm((f) => ({ ...f, event: e.target.value }))} placeholder={claimRelated ? "Deployed to Iraq / IED concussion / Discharge medical exam..." : "Started new med / ER visit / Last MRI / Symptom started..."} data-testid="ime-tl-event" />
          </label>
          <label className="field">
            <span className="field-label">SOURCE (optional)</span>
            <input className="field-input" type="text" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder={claimRelated ? "DD-214 / STR / personal recollection" : "Patient portal / personal notes / pharmacy"} data-testid="ime-tl-source" />
          </label>
          <button type="button" className="btn btn--accent btn--sm" onClick={addEvent} data-testid="ime-tl-add" disabled={!form.event.trim()}>
            <Plus size={11} weight="bold" /> ADD
          </button>
        </div>
        <table className="table--cards-on-mobile" style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }} data-testid="ime-tl-table">
          <thead>
            <tr style={{ background: "var(--surface-2)" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>DATE</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>EVENT</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>SOURCE</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {merged.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 14, fontStyle: "italic", color: "var(--text-secondary)" }}>No timeline events yet. Add one above, or skip — it&apos;s optional for routine visits.</td></tr>
            ) : merged.map((t) => (
              <tr key={t.id} data-testid={`ime-tl-row-${t.id}`} style={{ borderTop: "1px solid var(--border)" }}>
                <td data-label="DATE" style={{ padding: "8px 10px", fontFamily: "JetBrains Mono, monospace", fontSize: 11, whiteSpace: "nowrap" }}>{t.date || "—"}</td>
                <td data-label="EVENT" style={{ padding: "8px 10px", fontSize: 13, color: "var(--text-primary)" }}>{t.event}</td>
                <td data-label="SOURCE" style={{ padding: "8px 10px", fontSize: 12, color: "var(--text-secondary)" }}>
                  {t.source} {t.auto && <span className="pill" style={{ marginLeft: 4 }}>AUTO</span>}
                </td>
                <td data-label="" style={{ padding: "8px 10px", textAlign: "right" }}>
                  {!t.auto && (
                    <button type="button" onClick={() => removeEvent(t.id)} aria-label="Remove" data-testid={`ime-tl-remove-${t.id}`} style={{ background: "transparent", border: "none", color: "var(--error)", cursor: "pointer" }}>
                      <Trash size={13} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================ TALKING POINTS TAB
function TalkingPoints({ issues, imeIntake, patch, visitTypeDef }) {
  const checklists = imeIntake.checklists || {};
  const claimRelated = isClaimVisit(visitTypeDef);

  function toggle(issueId, itemId) {
    const next = { ...(checklists[issueId] || {}) };
    next[itemId] = !next[itemId];
    patch({ checklists: { ...checklists, [issueId]: next } });
  }

  if (issues.length === 0) {
    return (
      <div className="panel" data-testid="ime-checklists-empty">
        <div className="panel-header">
          <ListChecks size={16} weight="bold" />
          <span>TALKING POINTS</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc" style={{ margin: 0 }}>
            {claimRelated
              ? "Add at least one condition in the Condition Library to generate a curated checklist tailored to its theory of service connection."
              : "Add at least one condition in the Condition Library to see suggested talking points (symptoms to mention, side effects to flag, lifestyle factors)."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" data-testid="ime-checklists">
      <div className="panel-header">
        <ListChecks size={16} weight="bold" />
        <span>TALKING POINTS — PER CONDITION</span>
      </div>
      <div className="panel-body">
        <p className="panel-desc" style={{ marginTop: 0 }}>
          {claimRelated
            ? "For each condition the provider will be asked to opine on, mark off the supporting evidence you're bringing. Items vary by theory: direct, secondary, aggravation, and presumptive each need different supporting evidence."
            : "For each ongoing condition, tick the points you want to bring up. These flow into the packet so you don't lose track during a 15-minute visit."}
        </p>
        {issues.map((iss) => {
          const items = getTalkingPoints(iss, checklists[iss.id] || {}, visitTypeDef);
          const completed = items.filter((it) => it.done).length;
          return (
            <div key={iss.id} data-testid={`ime-checklist-${iss.id}`} style={{ marginTop: 18, padding: 16, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
                <strong style={{ fontSize: 14, color: "var(--text-primary)" }}>
                  {iss.claimedAs} <span className="mono" style={{ color: "var(--text-secondary)", fontWeight: 500 }}>DC {iss.code}{claimRelated ? ` · ${iss.theory}` : ""}</span>
                </strong>
                <span className="mono header-count">{completed}/{items.length}</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {items.map((it) => (
                  <li key={it.id}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", padding: "6px 8px", borderRadius: 6 }}>
                      <input
                        type="checkbox"
                        checked={!!it.done}
                        onChange={() => toggle(iss.id, it.id)}
                        data-testid={`ime-check-${iss.id}-${it.id}`}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: 13, color: "var(--text-primary)", textDecoration: it.done ? "line-through" : "none", opacity: it.done ? 0.7 : 1 }}>{it.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================ RECORDS / DOCUMENT MANAGER TAB
function DocumentManager({ evidence, imeIntake, patch }) {
  const docs = imeIntake.documents || [];
  const [form, setForm] = useState({ title: "", date: "", source: "", summary: "" });

  function add() {
    if (!form.title.trim()) return;
    patch({ documents: [...docs, { id: `doc-${Date.now()}`, ...form }] });
    setForm({ title: "", date: "", source: "", summary: "" });
  }
  function remove(id) {
    patch({ documents: docs.filter((d) => d.id !== id) });
  }

  return (
    <div className="panel" data-testid="ime-documents">
      <div className="panel-header">
        <FileText size={16} weight="bold" />
        <span>RECORDS TO BRING</span>
        <span className="header-count mono">{evidence.length + docs.length} total</span>
      </div>
      <div className="panel-body">
        <p className="panel-desc" style={{ marginTop: 0 }}>
          List every record you&apos;re bringing or sending ahead — labs, imaging, prior visit notes, medication lists, lay statements, audiograms, sleep studies. Anything already attached in the Claim Tracker is auto-listed; add anything else here. This becomes the appendix manifest in your packet.
        </p>

        {evidence.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)", marginTop: 14, marginBottom: 6 }}>From Claim Tracker (auto)</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {evidence.map((ev) => (
                <li key={ev.id} style={{ padding: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} data-testid={`ime-evidence-${ev.id}`}>
                  <strong style={{ fontSize: 13, color: "var(--text-primary)" }}>{ev.title || ev.label || "Untitled evidence"}</strong>
                  <div style={{ color: "var(--text-secondary)", fontSize: 11, marginTop: 2 }}>{ev.source || ev.type || "Veteran-supplied"} · {ev.date || ev.dateOfRecord || "—"}</div>
                </li>
              ))}
            </ul>
          </>
        )}

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)", marginTop: 20, marginBottom: 6 }}>Additional records</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr auto", gap: 8, alignItems: "end", padding: "10px 0", borderTop: "1px dashed var(--border)" }}>
          <label className="field">
            <span className="field-label">TITLE</span>
            <input className="field-input" type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="2019 audiogram from Dr. Smith" data-testid="ime-doc-title" />
          </label>
          <label className="field">
            <span className="field-label">DATE</span>
            <input className="field-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="ime-doc-date" />
          </label>
          <label className="field">
            <span className="field-label">SOURCE / TYPE</span>
            <input className="field-input" type="text" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder="Civilian audiologist / VA C&P / Buddy statement" data-testid="ime-doc-source" />
          </label>
          <button type="button" className="btn btn--accent btn--sm" onClick={add} disabled={!form.title.trim()} data-testid="ime-doc-add">
            <Plus size={11} weight="bold" /> ADD
          </button>
        </div>
        <label className="field" style={{ marginTop: 8 }}>
          <span className="field-label">SUMMARY (optional)</span>
          <input className="field-input" type="text" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} placeholder="One-line summary so the provider knows what's in it" data-testid="ime-doc-summary" />
        </label>

        {docs.length > 0 && (
          <table className="table--cards-on-mobile" style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }} data-testid="ime-doc-table">
            <thead>
              <tr style={{ background: "var(--surface-2)" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>TITLE</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>DATE</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>SOURCE</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text-secondary)" }}>SUMMARY</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`ime-doc-row-${d.id}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td data-label="TITLE" style={{ padding: "8px 10px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{d.title}</td>
                  <td data-label="DATE" style={{ padding: "8px 10px", fontSize: 11, fontFamily: "JetBrains Mono, monospace", whiteSpace: "nowrap" }}>{d.date || "—"}</td>
                  <td data-label="SOURCE" style={{ padding: "8px 10px", fontSize: 12, color: "var(--text-secondary)" }}>{d.source || "—"}</td>
                  <td data-label="SUMMARY" style={{ padding: "8px 10px", fontSize: 12, color: "var(--text-primary)" }}>{d.summary || "—"}</td>
                  <td data-label="" style={{ padding: "8px 10px", textAlign: "right" }}>
                    <button type="button" onClick={() => remove(d.id)} aria-label="Remove" data-testid={`ime-doc-remove-${d.id}`} style={{ background: "transparent", border: "none", color: "var(--error)", cursor: "pointer" }}>
                      <Trash size={13} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
